import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import store from '../store/store';
import type { RootState } from '../store/store';
import DashboardLayout from './DashboardLayout';
import {
  collectBakingSievesAndMagnetsRecord,
  savesectionApicall,
  fetchCycleData,
  type BakingSievesAndMagnetsCycleData as FetchedCycleData,
} from '../Services/BakingSievesAndMagnetsRecord';
import type { BakingSievesAndMagnetsCycleData, ChecklistItem } from '../store/BakingSievesAndMagnetsSlice';
import { addOfflineData, setFetchedCycles } from '../store/BakingSievesAndMagnetsSlice';
import { showOfflineSaveAlertForCategory } from '../utils/offlineAlerts';
import { mergePlanTourEmployee } from '../utils/mergePlanTourEmployee';
import { isBakeryNoidaEmployee } from '../constants/qualityDepartment';
import moment from 'moment';

const STORAGE_KEY = 'bakingSievesAndMagnetsInitialData';
const CYCLE_DATA_KEY = 'baking-sieves-magnets-cycle';

const DEFAULT_CHECKLIST_ITEMS: { criteria: string; title: string }[] = [
  { criteria: 'sieve_stress_grains', title: 'Sieve (After Stress Grains Sieving) - L10' },
  { criteria: 'sieve_maida_sieving', title: 'Sieve (After Maida Sieving Sieving) - L9' },
  { criteria: 'mix_or_after', title: 'Mix or After - G0 (MT)' },
  { criteria: 'metal_detector', title: 'Metal Detector' },
  { criteria: 'elevator_sifter', title: 'Elevator Sifter' },
  { criteria: 'check_in_sifter', title: 'Check in Sifter  (L.T. or L.V. Available)' },
  { criteria: 'elevator_sifter_gmt', title: 'Elevator Sifter G (GMT)' },
  { criteria: 'heavy_duty', title: 'Heavy Duty - Second Sifter' },
  { criteria: 'bread_pre_mix_plant', title: 'Bread pre-mix Plant' },
  { criteria: 'sugar_sieving_bsm', title: 'Sugar Sieving-Bsm' },
  { criteria: 'paddle_mixer_l2', title: 'Paddle Mixer (L-2)' },
  { criteria: 'grinding_setup_l6', title: 'Grinding Setup L-6' },
  { criteria: 'reworking_sieve_l7', title: 'Reworking Sieve (L-7)' },
  { criteria: 'condiment_batch_mixer_zone1', title: '1. Condiment Batch Mixer, Zone-1' },
  { criteria: 'oil_batching_system_zone2', title: '2. Oil Batching system, Zone-2' },
  { criteria: 'condiment_batch_mixer_auto', title: '3. Condiment Batch Mixer, auto-1' },
  { criteria: 'salt_storage_2', title: 'Salt Storage-2' },
  { criteria: 'flavour_room', title: 'Flavour Room' },
];

/** Used when plant/department is Bakery - Noida (same 6 lines as Sieve new plant Noida). */
const BAKERY_NOIDA_BAKING_CHECKLIST: { criteria: string; title: string }[] = [
  { criteria: 'noida_sugar_sifter', title: 'Sugar Sifter' },
  { criteria: 'noida_chemical_sifter_4_smp', title: 'Chemical Sifter 4 (SMP)' },
  { criteria: 'noida_sugar_grinding_room', title: 'Sugar Grinding Room' },
  { criteria: 'noida_packing_temp_l5', title: 'Packing Temp L-5' },
  { criteria: 'noida_packing_temp_l6', title: 'Packing Temp L-6' },
  { criteria: 'noida_packing_temp_l7', title: 'Packing Temp L-7' },
];

interface InitialData {
  timeValue: string;
  executiveName: string;
}

function getInitialData(): InitialData | null {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    return s ? (JSON.parse(s) as InitialData) : null;
  } catch {
    return null;
  }
}

function saveInitialData(data: InitialData): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn('Could not save Baking Sieves And Magnets initial data', e);
  }
}

function formatObservedTime(): string {
  return moment().format('DD/MM/YYYY hh:mm:ss A');
}

const MAX_CYCLES = 100;

const emptyStartForm = { timeValue: '', executiveName: '' };

function idx(cycle: number) {
  return Math.max(0, Math.min(cycle - 1, MAX_CYCLES - 1));
}

function buildChecklistForEmployee(employee: any | null | undefined): ChecklistItem[] {
  const items = isBakeryNoidaEmployee(employee) ? BAKERY_NOIDA_BAKING_CHECKLIST : DEFAULT_CHECKLIST_ITEMS;
  return items.map((item) => ({
    criteria: item.criteria,
    title: item.title,
    status: null,
    remarks: '',
  }));
}

function createEmptyChecklistFromStore(): ChecklistItem[] {
  const s = store.getState();
  const merged = mergePlanTourEmployee(s.planTour?.employeeDetails, s.planTour?.offlineEmployeeDetails);
  return buildChecklistForEmployee(merged);
}

const BakingSievesAndMagnets: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const rawEmployee = useSelector((state: RootState) => state.planTour?.employeeDetails);
  const offlineEmployee = useSelector((state: RootState) => state.planTour?.offlineEmployeeDetails);
  const mergedEmployee = useMemo(
    () => mergePlanTourEmployee(rawEmployee, offlineEmployee),
    [rawEmployee, offlineEmployee]
  );
  const employeeLocationKey = `${mergedEmployee?.departmentName ?? mergedEmployee?.DepartmentName ?? ''}|${mergedEmployee?.plantName ?? mergedEmployee?.PlantName ?? ''}`;

  const { plantTourId } = useSelector((state: RootState) => state.planTour ?? {});
  const user = useSelector((state: RootState) => state.user?.user);
  const isOfflineStarted = useSelector((state: RootState) => state.appState?.isOfflineStarted ?? false);
  const reduxFetchedCycles = useSelector((state: RootState) => state.bakingSievesAndMagnets?.fetchedCycles ?? []);

  const offlineInitial = useMemo(() => {
    try {
      const s = store.getState();
      if (!s?.appState?.isOfflineStarted) return null;
      const fetched = s?.bakingSievesAndMagnets?.fetchedCycles ?? [];
      if (!Array.isArray(fetched) || fetched.length === 0) return null;
      const byCycle: BakingSievesAndMagnetsCycleData[][] = Array(MAX_CYCLES).fill(null).map(() => []);
      fetched.forEach((cycleData: BakingSievesAndMagnetsCycleData) => {
        const cycleNumStr = String(cycleData.cycleNum || '1').replace('Cycle-', '').trim();
        const cycleNum = parseInt(cycleNumStr, 10);
        if (isNaN(cycleNum) || cycleNum < 1) return;
        const i = idx(cycleNum);
        if (i >= 0 && i < MAX_CYCLES) {
          byCycle[i].push({
            cycleNum: String(cycleNum),
            timeValue: cycleData.timeValue || '',
            executiveName: cycleData.executiveName || '',
            observedtime: cycleData.observedtime || '',
            items: cycleData.items || [],
          });
        }
      });
      const completedCycles = Array(MAX_CYCLES).fill(false);
      byCycle.forEach((sessions, index) => { if (sessions.length > 0) completedCycles[index] = true; });
      const maxCycleFromData = byCycle.reduce((max, sessions, index) => (sessions.length > 0 ? Math.max(max, index + 1) : max), 0);
      return { savedCycles: byCycle, cycleCompleted: completedCycles, currentCycle: maxCycleFromData + 1 };
    } catch {
      return null;
    }
  }, []);

  const [cycleCompleted, setCycleCompleted] = useState<boolean[]>(
    () => offlineInitial?.cycleCompleted ?? Array(MAX_CYCLES).fill(false)
  );
  const [isSessionStarted, setIsSessionStarted] = useState<boolean[]>(() => Array(MAX_CYCLES).fill(false));
  const [cycleExpanded, setCycleExpanded] = useState<boolean[]>(() => Array(MAX_CYCLES).fill(false));
  const [savedCycles, setSavedCycles] = useState<BakingSievesAndMagnetsCycleData[][]>(
    () => offlineInitial?.savedCycles ?? Array(MAX_CYCLES).fill(null).map(() => [])
  );
  const [showStartFormForNewProduct, setShowStartFormForNewProduct] = useState<boolean[]>(() => Array(MAX_CYCLES).fill(false));
  const [formDataByCycle, setFormDataByCycle] = useState<typeof emptyStartForm[]>(
    () => Array(MAX_CYCLES).fill(null).map(() => ({ ...emptyStartForm }))
  );
  const [observedtimeByCycle, setObservedtimeByCycle] = useState<string[]>(
    () => Array(MAX_CYCLES).fill('').map(() => '')
  );
  const [checklistByCycle, setChecklistByCycle] = useState<ChecklistItem[][]>(
    () => Array(MAX_CYCLES).fill(null).map(() => createEmptyChecklistFromStore())
  );
  const [currentCycle, setCurrentCycle] = useState(() => offlineInitial?.currentCycle ?? 1);
  const [expandedCompletedCycles, setExpandedCompletedCycles] = useState<Set<number>>(new Set());

  const bakeryChecklistProgressRef = useRef({ isSessionStarted, savedCycles });
  bakeryChecklistProgressRef.current = { isSessionStarted, savedCycles };

  /** When employee plant/dept is resolved (e.g. offline snapshot), swap empty cycles to Noida 6-line list or default. */
  useLayoutEffect(() => {
    setChecklistByCycle((prev) => {
      const merged = mergePlanTourEmployee(
        store.getState().planTour?.employeeDetails,
        store.getState().planTour?.offlineEmployeeDetails
      );
      const template = buildChecklistForEmployee(merged);
      const { isSessionStarted: iss, savedCycles: sc } = bakeryChecklistProgressRef.current;
      return prev.map((cl, i) => {
        if (iss[i] || (sc[i] && sc[i].length > 0)) return cl;
        if (
          cl.length === template.length &&
          cl.every((c, j) => c.criteria === template[j]?.criteria)
        ) {
          return cl;
        }
        return template.map((t) => ({ ...t, status: null, remarks: '' }));
      });
    });
  }, [employeeLocationKey]);

  const setCycleCompletedAt = (cycle: number, value: boolean) =>
    setCycleCompleted((prev) => { const n = [...prev]; n[idx(cycle)] = value; return n; });
  const setIsSessionStartedAt = (cycle: number, value: boolean) =>
    setIsSessionStarted((prev) => { const n = [...prev]; n[idx(cycle)] = value; return n; });
  const setCycleExpandedAt = (cycle: number, value: boolean) =>
    setCycleExpanded((prev) => { const n = [...prev]; n[idx(cycle)] = value; return n; });
  const setSavedCyclesAt = (cycle: number, updater: (prev: BakingSievesAndMagnetsCycleData[]) => BakingSievesAndMagnetsCycleData[]) =>
    setSavedCycles((prev) => { const n = prev.map((a) => [...a]); n[idx(cycle)] = updater(n[idx(cycle)]); return n; });
  const setShowStartFormForNewProductAt = (cycle: number, value: boolean) =>
    setShowStartFormForNewProduct((prev) => { const n = [...prev]; n[idx(cycle)] = value; return n; });
  const setFormDataByCycleAt = (cycle: number, value: typeof emptyStartForm) =>
    setFormDataByCycle((prev) => { const n = prev.map((x) => ({ ...x })); n[idx(cycle)] = value; return n; });
  const setObservedtimeAt = (cycle: number, value: string) =>
    setObservedtimeByCycle((prev) => { const n = [...prev]; n[idx(cycle)] = value; return n; });
  const setChecklistByCycleAt = (cycle: number, updater: (prev: ChecklistItem[]) => ChecklistItem[]) =>
    setChecklistByCycle((prev) => { const n = prev.map((x) => [...x]); n[idx(cycle)] = updater(n[idx(cycle)]); return n; });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    cycle: number
  ) => {
    const { name, value } = e.target;
    const i = idx(cycle);
    setFormDataByCycle((prev) => { const n = prev.map((x) => ({ ...x })); n[i] = { ...n[i], [name]: value }; return n; });
  };

  const handleChecklistItemChange = (
    cycle: number,
    itemIndex: number,
    field: 'status' | 'remarks',
    value: any
  ) => {
    setChecklistByCycleAt(cycle, (prev) =>
      prev.map((item, idx) => (idx === itemIndex ? { ...item, [field]: value } : item))
    );
  };

  const handleStartSession = (cycle: number = 1) => {
    const data = formDataByCycle[idx(cycle)];
    saveInitialData({
      timeValue: data.timeValue || '',
      executiveName: data.executiveName || '',
    });
    setObservedtimeAt(cycle, formatObservedTime());
    setIsSessionStartedAt(cycle, true);
  };

  const handleCancelSession = (cycle: number = 1) => {
    setIsSessionStartedAt(cycle, false);
    setChecklistByCycleAt(cycle, () => createEmptyChecklistFromStore());
  };

  const handleSaveSession = async (cycle: number = 1) => {
    if (!plantTourId) {
      alert('Plant Tour ID is missing. Please start a plant tour first.');
      return;
    }
    const i = idx(cycle);
    const startData = formDataByCycle[i];
    const checklist = checklistByCycle[i] || [];
    const timeValue = (startData.timeValue || '').trim() || 'N/A';
    const executiveName = (startData.executiveName || '').trim() || 'N/A';
    const observedDateTime = observedtimeByCycle[i] || formatObservedTime();
    const userName = user?.Name || null;

    const records = checklist
      .filter(item => item.status !== null)
      .map((item) =>
        collectBakingSievesAndMagnetsRecord(
          cycle,
          item.criteria,
          item.title,
          timeValue,
          executiveName,
          item.status === 'not-okay' ? (item.remarks || 'Defect noted') : null,
          plantTourId,
          userName
        )
      );

    if (records.length === 0) {
      alert('Please mark at least one item as Okay or Not Okay before saving.');
      return;
    }

    const savedSession: BakingSievesAndMagnetsCycleData = {
      cycleNum: String(cycle),
      timeValue,
      executiveName,
      observedtime: observedDateTime,
      items: checklist.filter(item => item.status !== null).map((item) => ({
        criteria: item.criteria,
        title: item.title,
        status: item.status,
        remarks: item.remarks || '',
      })),
    };

    try {
      if (isOfflineStarted) {
        dispatch(addOfflineData({ cycleNo: cycle, records: [savedSession], timestamp: Date.now() }));
        setSavedCyclesAt(cycle, (prev) => [...prev, savedSession]);
        setIsSessionStartedAt(cycle, false);
        setShowStartFormForNewProductAt(cycle, false);
        try {
          const key = `${CYCLE_DATA_KEY}-${cycle}-data`;
          const existing = localStorage.getItem(key);
          const existingArr = existing ? (JSON.parse(existing) as any[]) : [];
          localStorage.setItem(key, JSON.stringify(Array.isArray(existingArr) ? [...existingArr, ...records] : records));
        } catch (_) {}
        showOfflineSaveAlertForCategory(cycle, 'Baking Sieves And Magnets');
      } else {
        const result = await savesectionApicall(records);
        if (result.success) {
          setSavedCyclesAt(cycle, (prev) => [...prev, savedSession]);
          setIsSessionStartedAt(cycle, false);
          setShowStartFormForNewProductAt(cycle, false);
          setChecklistByCycleAt(cycle, () => createEmptyChecklistFromStore());
        } else {
          alert(`Failed to save Cycle ${cycle}: ${result.message}`);
        }
      }
    } catch (error) {
      console.error('Error saving session:', error);
      alert(`Error: ${error instanceof Error ? error.message : 'Failed to save session.'}`);
    }
  };

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/home');
  };

  const handleCompleteCycle = (cycle: number) => {
    const sessions = savedCycles[idx(cycle)];
    if (!sessions || sessions.length === 0) {
      alert('Please save at least one session before completing the cycle.');
      return;
    }
    setCycleCompletedAt(cycle, true);
    setIsSessionStartedAt(cycle, false);
    setShowStartFormForNewProductAt(cycle, false);
    if (cycle === currentCycle) setCurrentCycle((prev) => prev + 1);
  };

  const handleAddNewProduct = (cycle: number) => {
    const initial = getInitialData();
    setShowStartFormForNewProductAt(cycle, true);
    setFormDataByCycleAt(cycle, {
      timeValue: initial?.timeValue ?? '',
      executiveName: initial?.executiveName ?? '',
    });
    setChecklistByCycleAt(cycle, () => createEmptyChecklistFromStore());
    setObservedtimeAt(cycle, formatObservedTime());
  };

  const toggleCompletedCycleExpansion = (cycleNum: number) => {
    setExpandedCompletedCycles((prev) => {
      const next = new Set(prev);
      if (next.has(cycleNum)) next.delete(cycleNum);
      else next.add(cycleNum);
      return next;
    });
  };

  const loadCycleData = async () => {
    if (!plantTourId || isOfflineStarted) return;
    try {
      const cycles = await fetchCycleData(plantTourId);
      const mapped: BakingSievesAndMagnetsCycleData[] = cycles.map((c: FetchedCycleData) => ({
        cycleNum: c.cycleNum,
        timeValue: c.timeValue,
        executiveName: c.executiveName,
        observedtime: c.observedtime,
        items: c.items || [],
      }));
      dispatch(setFetchedCycles(mapped));
      const byCycle: BakingSievesAndMagnetsCycleData[][] = Array(MAX_CYCLES).fill(null).map(() => []);
      cycles.forEach((cycleData: FetchedCycleData) => {
        const cycleNum = parseInt(cycleData.cycleNum, 10);
        const i = idx(cycleNum);
        if (i >= 0 && i < MAX_CYCLES) byCycle[i].push({
          cycleNum: cycleData.cycleNum,
          timeValue: cycleData.timeValue,
          executiveName: cycleData.executiveName,
          observedtime: cycleData.observedtime,
          items: cycleData.items || [],
        });
      });
      setSavedCycles(byCycle);
      setCycleCompleted((prev) => prev.map((v, i) => (byCycle[i].length > 0 ? true : v)));
      const maxCycleFromData = cycles.length ? Math.max(...cycles.map((cd) => parseInt(String(cd.cycleNum), 10) || 0), 1) : 0;
      setCurrentCycle(maxCycleFromData + 1);
    } catch (error) {
      console.error('Error loading cycle data:', error);
    }
  };

  useLayoutEffect(() => {
    if (!plantTourId || !isOfflineStarted) return;
    const fetched = store.getState()?.bakingSievesAndMagnets?.fetchedCycles ?? [];
    if (!Array.isArray(fetched) || fetched.length === 0) return;
    const byCycle: BakingSievesAndMagnetsCycleData[][] = Array(MAX_CYCLES).fill(null).map(() => []);
    fetched.forEach((cycleData: BakingSievesAndMagnetsCycleData) => {
      const cycleNumStr = String(cycleData.cycleNum || '1').replace('Cycle-', '').trim();
      const cycleNum = parseInt(cycleNumStr, 10);
      if (isNaN(cycleNum) || cycleNum < 1) return;
      const i = idx(cycleNum);
      if (i >= 0 && i < MAX_CYCLES) {
        byCycle[i].push({
          cycleNum: String(cycleNum),
          timeValue: cycleData.timeValue || '',
          executiveName: cycleData.executiveName || '',
          observedtime: cycleData.observedtime || '',
          items: cycleData.items || [],
        });
      }
    });
    setSavedCycles(byCycle);
    setCycleCompleted(byCycle.map((sessions) => sessions.length > 0));
    const maxCycleFromData = byCycle.reduce((max, sessions, index) => (sessions.length > 0 ? Math.max(max, index + 1) : max), 0);
    setCurrentCycle(maxCycleFromData + 1);
  }, [plantTourId, isOfflineStarted]);

  useEffect(() => {
    if (!plantTourId) return;
    if (isOfflineStarted) {
      const cyclesToUse = store.getState()?.bakingSievesAndMagnets?.fetchedCycles ?? [];
      if (!Array.isArray(cyclesToUse) || cyclesToUse.length === 0) {
        setSavedCycles(Array(MAX_CYCLES).fill(null).map(() => []));
        setCycleCompleted(Array(MAX_CYCLES).fill(false));
        setCurrentCycle(1);
        return;
      }
      const byCycle: BakingSievesAndMagnetsCycleData[][] = Array(MAX_CYCLES).fill(null).map(() => []);
      cyclesToUse.forEach((cycleData: BakingSievesAndMagnetsCycleData) => {
        const cycleNumStr = String(cycleData.cycleNum || '1').replace('Cycle-', '').trim();
        const cycleNum = parseInt(cycleNumStr, 10);
        if (isNaN(cycleNum) || cycleNum < 1) return;
        const i = idx(cycleNum);
        if (i >= 0 && i < MAX_CYCLES) {
          byCycle[i].push({
            cycleNum: String(cycleNum),
            timeValue: cycleData.timeValue || '',
            executiveName: cycleData.executiveName || '',
            observedtime: cycleData.observedtime || '',
            items: cycleData.items || [],
          });
        }
      });
      setSavedCycles(byCycle);
      setCycleCompleted(byCycle.map((sessions) => sessions.length > 0));
      const maxCycleFromData = byCycle.reduce((max, sessions, index) => (sessions.length > 0 ? Math.max(max, index + 1) : max), 0);
      setCurrentCycle(maxCycleFromData + 1);
      return;
    }
    loadCycleData();
  }, [plantTourId, isOfflineStarted, reduxFetchedCycles?.length]);

  useEffect(() => {
    const initial = getInitialData();
    if (!initial?.timeValue && !initial?.executiveName) return;
    setFormDataByCycle((prev) =>
      prev.map((p, i) => {
        const cycleNum = i + 1;
        if (cycleNum === 1) {
          if (p.timeValue || p.executiveName) return p;
          return { timeValue: initial?.timeValue ?? '', executiveName: initial?.executiveName ?? '' };
        }
        if (!cycleCompleted[cycleNum - 2]) return p;
        if (p.timeValue || p.executiveName) return p;
        return { timeValue: initial?.timeValue ?? '', executiveName: initial?.executiveName ?? '' };
      })
    );
  }, [cycleCompleted]);

  const headerDate = moment().format('DD/MM/YYYY');
  const shiftValue = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('shiftValue') || 'Shift 1' : 'Shift 1';

  const renderCompleteSummary = (session: BakingSievesAndMagnetsCycleData) => {
    const defects = session.items.filter(item => item.status === 'not-okay');
    const okays = session.items.filter(item => item.status === 'okay');

    return (
      <div className="space-y-4">
        {/* Defects Table */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-4 py-2">
            <h3 className="text-sm font-semibold text-red-600">Defects</h3>
          </div>
          <div className="px-4">
            <div className="grid grid-cols-2 gap-4 py-2 border-b border-gray-200">
              <span className="text-sm text-gray-500">Title</span>
              <span className="text-sm text-gray-500">Remarks</span>
            </div>
            {defects.length > 0 ? (
              defects.map((item, index) => (
                <div key={index} className="grid grid-cols-2 gap-4 py-3 border-b border-gray-100 last:border-b-0">
                  <span className="text-sm text-gray-800">{item.title}</span>
                  <span className="text-sm text-gray-600">{item.remarks || '-'}</span>
                </div>
              ))
            ) : (
              <div className="py-3 text-sm text-gray-500">No defects reported</div>
            )}
          </div>
        </div>

        {/* Okays and Defects Summary */}
        <div className="grid grid-cols-2 gap-4">
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-2 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-teal-600">Okays</h3>
            </div>
            <div className="px-4 py-3">
              {okays.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {okays.map((item, index) => (
                    <span key={index} className="text-sm text-gray-700">{item.title}</span>
                  )).reduce((prev: React.ReactNode[], curr, i) => {
                    if (i > 0) prev.push(<span key={`sep-${i}`} className="text-gray-400 mx-1">|</span>);
                    prev.push(curr);
                    return prev;
                  }, [] as React.ReactNode[])}
                </div>
              ) : (
                <span className="text-sm text-gray-500">None</span>
              )}
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-2 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700">Defects</h3>
            </div>
            <div className="px-4 py-3">
              {defects.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {defects.map((item, index) => (
                    <span key={index} className="text-sm text-gray-700">{item.title}</span>
                  )).reduce((prev: React.ReactNode[], curr, i) => {
                    if (i > 0) prev.push(<span key={`sep-${i}`} className="text-gray-400 mx-1">|</span>);
                    prev.push(curr);
                    return prev;
                  }, [] as React.ReactNode[])}
                </div>
              ) : (
                <span className="text-sm text-gray-500">None</span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="w-full px-4 md:px-6 py-6">
        {/* Back Button */}
        <div className="mb-4">
          <button type="button" onClick={handleBack} className="flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium bg-transparent border-0 cursor-pointer p-0">
            <span className="text-lg">←</span>
            <span>Back</span>
          </button>
        </div>

        {/* Header Section */}
        <div className="flex items-center justify-between py-3 mb-4 bg-gray-50 px-4 rounded">
          <div className="flex items-center gap-3">
            <h1 className="text-base font-semibold text-gray-800">Sieves And Magnets</h1>
            <span className="flex items-center gap-1 text-sm text-gray-600">
              <span className="w-2 h-2 rounded-full bg-green-500"></span>
              {shiftValue}
            </span>
            <span className="text-sm text-gray-500">• {headerDate}</span>
          </div>
          <button type="button" className="p-1 text-gray-500 hover:text-gray-700">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
            </svg>
          </button>
        </div>

        <div className="w-full space-y-4">
          {/* Completed Cycles */}
          {Array.from({ length: MAX_CYCLES }, (_, i) => i + 1)
            .filter((n) => n < currentCycle && (cycleCompleted[idx(n)] || savedCycles[idx(n)].length > 0))
            .map((n) => (
              <div key={n} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div 
                  className="flex items-center justify-between px-4 py-3 cursor-pointer" 
                  onClick={() => toggleCompletedCycleExpansion(n)}
                >
                  <h2 className="text-base font-semibold text-gray-800">Cycle {n}</h2>
                  <svg 
                    className={`w-5 h-5 text-gray-400 transition-transform ${expandedCompletedCycles.has(n) ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {expandedCompletedCycles.has(n) && savedCycles[idx(n)].length > 0 && (
                  <div className="px-4 pb-4">
                    {savedCycles[idx(n)].map((session, index) => (
                      <div key={index} className="mt-4 first:mt-0">
                        {renderCompleteSummary(session)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

          {/* Current Cycle Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* Start Section - Before session started */}
            {!isSessionStarted[idx(currentCycle)] && !cycleCompleted[idx(currentCycle)] && (savedCycles[idx(currentCycle)].length === 0 || showStartFormForNewProduct[idx(currentCycle)]) && (
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-base font-semibold text-gray-800">Cycle {currentCycle}</h2>
                  <div className="text-sm text-gray-500">Executive Name</div>
                </div>

                {/* Top row - Executive Name and Time */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <input
                      type="text"
                      name="executiveName"
                      value={formDataByCycle[idx(currentCycle)].executiveName}
                      onChange={(e) => handleInputChange(e, currentCycle)}
                      placeholder="Enter Executive Name"
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <input
                      type="time"
                      name="timeValue"
                      value={formDataByCycle[idx(currentCycle)].timeValue}
                      onChange={(e) => handleInputChange(e, currentCycle)}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Checklist Items */}
                <div className="space-y-0">
                  {(checklistByCycle[idx(currentCycle)] || []).map((item, itemIndex) => (
                    <div key={itemIndex} className="py-3 border-b border-gray-100 last:border-b-0">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <span className="text-sm text-gray-800">{item.title}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleChecklistItemChange(currentCycle, itemIndex, 'status', 'not-okay')}
                            className={`flex items-center gap-2 px-4 py-1.5 rounded-full border text-sm transition-colors ${
                              item.status === 'not-okay'
                                ? 'bg-red-500 border-red-500 text-white'
                                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {item.status === 'not-okay' ? (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            ) : (
                              <span className="w-4 h-4 rounded-full border-2 border-red-400"></span>
                            )}
                            Not Okay
                          </button>
                          <button
                            type="button"
                            onClick={() => handleChecklistItemChange(currentCycle, itemIndex, 'status', 'okay')}
                            className={`flex items-center gap-2 px-4 py-1.5 rounded-full border text-sm transition-colors ${
                              item.status === 'okay'
                                ? 'bg-green-50 border-green-200'
                                : 'bg-white border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <span className="w-4 h-4 rounded-full border-2 border-teal-500"></span>
                            <span className="text-gray-700">Okay</span>
                          </button>
                        </div>
                      </div>
                      {item.status === 'not-okay' && (
                        <div className="mt-3">
                          <label className="block text-xs text-gray-500 mb-1">Major Defects and Remarks</label>
                          <input
                            type="text"
                            placeholder="Type Here..."
                            value={item.remarks}
                            onChange={(e) => handleChecklistItemChange(currentCycle, itemIndex, 'remarks', e.target.value)}
                            className="w-full border border-gray-200 rounded px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Bottom buttons */}
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => handleCancelSession(currentCycle)}
                    className="px-4 py-2 border border-gray-300 rounded text-sm font-medium text-gray-600 hover:bg-gray-50"
                  >
                    Next
                  </button>
                  <button
                    type="button"
                    onClick={() => handleStartSession(currentCycle)}
                    className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700"
                  >
                    Start Session
                  </button>
                </div>
              </div>
            )}

            {/* Save Section - After session started */}
            {isSessionStarted[idx(currentCycle)] && !cycleCompleted[idx(currentCycle)] && (
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-base font-semibold text-gray-800">Cycle {currentCycle}</h2>
                  <div className="text-sm text-gray-500">
                    Executive: {formDataByCycle[idx(currentCycle)].executiveName || '-'}
                  </div>
                </div>

                {/* Checklist Items for Save */}
                <div className="space-y-0">
                  {(checklistByCycle[idx(currentCycle)] || []).map((item, itemIndex) => (
                    <div key={itemIndex} className="py-3 border-b border-gray-100 last:border-b-0">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <span className="text-sm text-gray-800">{item.title}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleChecklistItemChange(currentCycle, itemIndex, 'status', 'not-okay')}
                            className={`flex items-center gap-2 px-4 py-1.5 rounded-full border text-sm transition-colors ${
                              item.status === 'not-okay'
                                ? 'bg-red-500 border-red-500 text-white'
                                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {item.status === 'not-okay' ? (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            ) : (
                              <span className="w-4 h-4 rounded-full border-2 border-red-400"></span>
                            )}
                            Not Okay
                          </button>
                          <button
                            type="button"
                            onClick={() => handleChecklistItemChange(currentCycle, itemIndex, 'status', 'okay')}
                            className={`flex items-center gap-2 px-4 py-1.5 rounded-full border text-sm transition-colors ${
                              item.status === 'okay'
                                ? 'bg-green-50 border-green-200'
                                : 'bg-white border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <span className="w-4 h-4 rounded-full border-2 border-teal-500"></span>
                            <span className="text-gray-700">Okay</span>
                          </button>
                        </div>
                      </div>
                      {item.status === 'not-okay' && (
                        <div className="mt-3">
                          <label className="block text-xs text-gray-500 mb-1">Major Defects and Remarks</label>
                          <input
                            type="text"
                            placeholder="Type Here..."
                            value={item.remarks}
                            onChange={(e) => handleChecklistItemChange(currentCycle, itemIndex, 'remarks', e.target.value)}
                            className="w-full border border-gray-200 rounded px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Bottom buttons */}
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => handleCancelSession(currentCycle)}
                    className="px-4 py-2 border border-gray-300 rounded text-sm font-medium text-gray-600 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSaveSession(currentCycle)}
                    className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700"
                  >
                    Save Session
                  </button>
                </div>
              </div>
            )}

            {/* After save: Add new session / Complete Cycle */}
            {savedCycles[idx(currentCycle)].length > 0 && !cycleCompleted[idx(currentCycle)] && !isSessionStarted[idx(currentCycle)] && !showStartFormForNewProduct[idx(currentCycle)] && (
              <div className="p-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-700 font-semibold mb-3">{savedCycles[idx(currentCycle)].length} session(s) saved in Cycle {currentCycle}</p>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => handleAddNewProduct(currentCycle)} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium">Add new session</button>
                    <button type="button" onClick={() => handleCompleteCycle(currentCycle)} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-medium">Complete Cycle & Move to Next</button>
                  </div>
                </div>
              </div>
            )}

            {/* Completed current cycle */}
            {cycleCompleted[idx(currentCycle)] && (
              <div className="p-4">
                <div 
                  className="flex items-center justify-between cursor-pointer" 
                  onClick={() => setCycleExpandedAt(currentCycle, !cycleExpanded[idx(currentCycle)])}
                >
                  <h2 className="text-base font-semibold text-gray-800">Cycle {currentCycle}</h2>
                  <svg 
                    className={`w-5 h-5 text-gray-400 transition-transform ${cycleExpanded[idx(currentCycle)] ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {cycleExpanded[idx(currentCycle)] && (
                  <div className="mt-4">
                    {savedCycles[idx(currentCycle)].map((session, index) => (
                      <div key={index} className="mt-4 first:mt-0">
                        {renderCompleteSummary(session)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BakingSievesAndMagnets;
